import { useQuery } from "@tanstack/react-query";
import { brokersApi } from "@/api/brokers";
import type { BrokerStatus } from "@/api/brokers";
import { leadsApi } from "@/api/leads";
import type { BrokerLeadSummaryDto } from "@/api/leads";
import { tasksApi } from "@/api/tasks";
import { territoriesApi } from "@/api/territories";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelRows } from "./shared";

/**
 * Broker-channel CRM panels absorbed from the retired Liaison / Marketer /
 * Manager dashboard pages: prospect + onboarding queues, credential alerts,
 * the daily task list, the lead work queues, and the territory roster.
 */

// ---- Liaison broker pipeline ------------------------------------------------

function useMyBrokers() {
  return useQuery({
    queryKey: ["dashboard", "my-brokers"],
    queryFn: () => brokersApi.list({ page: 1, pageSize: 100, assignedToMe: true }),
  });
}

function useMyBrokersWithStatus(status: BrokerStatus) {
  const pipeline = useMyBrokers();
  const rows = (pipeline.data?.data ?? []).filter((b) => b.status === status);
  return { pipeline, rows };
}

export function NewProspectsWidget({ widget }: DashboardWidgetProps) {
  const { pipeline, rows } = useMyBrokersWithStatus("Prospect");
  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/brokers"
      isLoading={pipeline.isLoading}
      isError={pipeline.isError}
      isEmpty={rows.length === 0}
      emptyText="No new prospects awaiting outreach."
    >
      <PanelRows
        items={rows.map((b) => ({
          id: b.id,
          primary: b.companyName,
          secondary: [b.name, b.state].filter(Boolean).join(" · ") || null,
          badge: "New",
          badgeTone: "info",
          href: `/brokers/${b.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function InOnboardingWidget({ widget }: DashboardWidgetProps) {
  const { pipeline, rows } = useMyBrokersWithStatus("Onboarding");
  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/brokers"
      isLoading={pipeline.isLoading}
      isError={pipeline.isError}
      isEmpty={rows.length === 0}
      emptyText="No brokers currently in onboarding."
    >
      <PanelRows
        items={rows.map((b) => ({
          id: b.id,
          primary: b.companyName,
          secondary: [b.name, b.state].filter(Boolean).join(" · ") || null,
          badge: "Onboarding",
          badgeTone: "progress",
          href: `/brokers/${b.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function ExpiringCredentialsWidget({ widget }: DashboardWidgetProps) {
  const alerts = useQuery({
    queryKey: ["dashboard", "broker-alerts"],
    queryFn: () => brokersApi.alerts(),
  });
  const rows = alerts.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/brokers"
      isLoading={alerts.isLoading}
      isError={alerts.isError}
      isEmpty={rows.length === 0}
      emptyText="All credentials current."
    >
      <PanelRows
        items={rows.map((a) => ({
          id: `${a.brokerId}-${a.alertType}`,
          primary: a.companyName,
          secondary: `${a.alertType === "EoInsurance" ? "E&O" : "License"} expiry`,
          badge: a.daysUntilExpiry < 0 ? "Expired" : `${a.daysUntilExpiry}d`,
          badgeTone:
            a.severity === "Expired" || a.severity === "Critical" ? "danger" : "warn",
          timestamp: a.expiresOn,
          href: `/brokers/${a.brokerId}`,
        }))}
      />
    </WidgetFrame>
  );
}

// ---- Daily tasks -------------------------------------------------------------

export function TodaysTasksWidget({ widget }: DashboardWidgetProps) {
  const tasks = useQuery({
    queryKey: ["dashboard", "tasks-today"],
    queryFn: () => tasksApi.today(),
  });
  const rows = tasks.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/tasks"
      isLoading={tasks.isLoading}
      isError={tasks.isError}
      isEmpty={rows.length === 0}
      emptyText="No tasks due today."
    >
      <PanelRows
        items={rows.map((t) => ({
          id: t.id,
          primary: t.title,
          badge: t.priority,
          badgeTone:
            t.priority === "Urgent" ? "danger" : t.priority === "High" ? "warn" : "neutral",
          timestamp: t.dueDate,
        }))}
      />
    </WidgetFrame>
  );
}

// ---- Lead work queues ----------------------------------------------------------

function daysSinceIso(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function useMyLeads() {
  return useQuery({
    queryKey: ["dashboard", "my-leads"],
    queryFn: () => leadsApi.list({ page: 1, pageSize: 100 }),
  });
}

function leadRowItems(rows: BrokerLeadSummaryDto[]) {
  return rows.map((l) => ({
    id: l.id,
    primary: `${l.firstName} ${l.lastName}`.trim(),
    secondary:
      [l.companyName, l.state, l.phone].filter(Boolean).join(" · ") || null,
    badge: l.status,
    href: `/leads/${l.id}`,
  }));
}

export function HotLeadsWidget({ widget }: DashboardWidgetProps) {
  const leads = useMyLeads();
  const rows = (leads.data?.data ?? []).filter(
    (l) =>
      l.interestLevel === "Hot" &&
      l.status !== "HandedOff" &&
      (!l.lastContactDate || daysSinceIso(l.lastContactDate) > 3),
  );

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/leads"
      isLoading={leads.isLoading}
      isError={leads.isError}
      isEmpty={rows.length === 0}
      emptyText="All hot leads contacted within the last 3 days."
    >
      <PanelRows items={leadRowItems(rows)} />
    </WidgetFrame>
  );
}

export function NewLeadsWidget({ widget }: DashboardWidgetProps) {
  const leads = useMyLeads();
  const rows = (leads.data?.data ?? []).filter((l) => l.status === "New").slice(0, 10);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/leads"
      isLoading={leads.isLoading}
      isError={leads.isError}
      isEmpty={rows.length === 0}
      emptyText="No new leads waiting."
    >
      <PanelRows items={leadRowItems(rows)} />
    </WidgetFrame>
  );
}

// ---- Territories ----------------------------------------------------------------

export function TerritoriesWidget({ widget }: DashboardWidgetProps) {
  const territories = useQuery({
    queryKey: ["dashboard", "territories"],
    queryFn: () => territoriesApi.list(),
  });
  const rows = territories.data ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/admin/territories"
      toLabel="Manage"
      isLoading={territories.isLoading}
      isError={territories.isError}
      isEmpty={rows.length === 0}
      emptyText="No territories configured."
    >
      <PanelRows
        items={rows.map((t) => ({
          id: t.id,
          primary: t.name,
          secondary: `${t.states.length} state${t.states.length === 1 ? "" : "s"} · ${
            t.liaisons.length
          } liaison${t.liaisons.length === 1 ? "" : "s"}`,
          badge: t.code,
        }))}
      />
    </WidgetFrame>
  );
}
